import {
	getAllCameraStorageKeys,
	getAllLatestImages,
	getDeviceLatestImage,
} from 'src/services/cam-cloud/recordings';
import { devices } from 'src/stores';
import { camera_previews } from 'src/components/Camera/stores/camera_previews';
import { camera_storage_keys } from 'src/components/Camera/stores/camera_storage_keys';
import { get } from 'svelte/store';
import type { CloudLatestImage } from '../types/cloud-recordings';

/**
 * Load Previews for all camera devices and store them in the camera_previews store
 * Guards against no camera devices in the account
 */
export const loadCameraDevicePreviews = async () => {
	let _devices = get(devices);
	if (!_devices) {
		return;
	}

	if (Object.values(_devices).filter((d: any) => d.category === 'camera').length === 0) {
		return;
	}

	const response = await getAllLatestImages().catch((err) => {
		console.error('[Camera Cloud] Error fetching camera device previews', err);
		return null;
	});

	const raw = ((response as any)?.data ?? response ?? {}) as Record<string, unknown>;
	return camera_previews.setFromRaw(raw);
};

/**
 * Loads every camera's encrypted `master_key` backup **history** and caches the
 * full array (newest first) per device in the {@link camera_storage_keys} store,
 * so key rotation is preserved and older recordings stay decryptable.
 */
export const loadCameraStorageKeys = async (): Promise<void> => {
	const response = await getAllCameraStorageKeys().catch((err) => {
		console.error('[Camera Cloud] Error fetching camera storage keys', err);
		return null;
	});

	if (!response) return;

	for (const [mac, entries] of Object.entries(response)) {
		const history = Array.isArray(entries)
			? entries
					.filter((entry) => entry?.blob && entry?.hmac)
					.map((entry) => ({ blob: entry.blob, hmac: entry.hmac, createdAt: entry.createdAt }))
			: [];
		if (history.length) {
			camera_storage_keys.setAll(mac, history);
		}
	}
};

/**
 * Runs the initial camera loading during account bootstrap: previews and
 * encrypted recording-key backups, in parallel.
 */
export const cameraServicesInitialLoad = async (): Promise<void> => {
	await Promise.allSettled([loadCameraDevicePreviews(), loadCameraStorageKeys()]);
};

export const loadCameraDevicePreview = async (deviceId: string) => {
	if (!deviceId) {
		return;
	}
	const response = await getDeviceLatestImage(deviceId).catch((err) => {
		console.warn(`Error fetching camera device (${deviceId}) preview`, err);
		return null;
	});

	// Safeguard against no image
	if (!response?.imageUrls?.large) {
		return {};
	}

	const preview: CloudLatestImage = {
		...response.imageUrls,
		encrypted: response.encrypted,
		recId: response.recordingId,
	};
	camera_previews.updateDevicePreview(deviceId, preview);

	return preview;
};
